import { useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { warn } from "@/lib/log";

const fail = (e) => ({ ok: false, error: formatApiError(e), status: e?.response?.status });

/* All auth mutations (login / MFA / logout / password). The session itself lives in the backend's httpOnly cookie. */
export default function useAuthActions(setUser) {
  const login = useCallback(async (email, password, rememberMe = true) => {
    try {
      const { data } = await api.post("/auth/login", { email: email.trim().toLowerCase(), password, remember_me: rememberMe });
      if (data.mfa_required) {
        return { ok: true, mfaRequired: true, mfaToken: data.mfa_token, maskedEmail: data.masked_email, emailSent: data.email_sent };
      }
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) { return fail(e); }
  }, [setUser]);

  const verifyMfa = useCallback(async (mfaToken, code, trustDevice) => {
    try {
      const { data } = await api.post("/auth/mfa/verify", { mfa_token: mfaToken, code, trust_device: trustDevice }, { retry: false });
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) { return fail(e); }
  }, [setUser]);

  const resendMfa = useCallback(async (mfaToken) => {
    try {
      const { data } = await api.post("/auth/mfa/resend", { mfa_token: mfaToken }, { retry: false });
      return { ok: true, mfaToken: data.mfa_token, emailSent: data.email_sent };
    } catch (e) { return fail(e); }
  }, []);

  const completeSignup = useCallback((data) => setUser(data.user), [setUser]);

  const signOut = useCallback(async (path) => {
    try { await api.post(path, null, { retry: false }); }
    catch (e) { warn(`${path} failed`, e?.message || e); }
    setUser(false);
  }, [setUser]);
  const logout = useCallback(() => signOut("/auth/logout"), [signOut]);
  const logoutAll = useCallback(() => signOut("/auth/logout-all"), [signOut]);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      const { data } = await api.post("/auth/password/change", { current_password: currentPassword, new_password: newPassword }, { retry: false });
      setUser(data.user);
      return { ok: true };
    } catch (e) { return fail(e); }
  }, [setUser]);

  return useMemo(() => ({ login, verifyMfa, resendMfa, completeSignup, logout, logoutAll, changePassword }),
    [login, verifyMfa, resendMfa, completeSignup, logout, logoutAll, changePassword]);
}
